import { locateOnLadder, ladderStatement } from './counterparty_ladder.js';
import { safeSanitize } from '../utils/safe_sanitize';
import { EFFECT_SEVERITY } from './counterparty_comment_effects';
import { hasLiftedClauseHtml, insertLiftedClause } from './clausebook_insert';
import {
  ANNOTATION_HIDDEN_SUBTREE_SELECTOR,
  unescapeFromProjection,
} from './document_text_index';
import { preparedRequests } from './document_patch_compiler';

// An annotation card is an inert snapshot of the paragraph, not a second live
// copy of it. `cleanHtml` (lease_editor_controller.js) enforces that for the
// tenant-import path and this mirrors its whitelist: the card is `$compile`d by
// `bind-html-compile` (comments.html.haml), so anything left in it is linked a
// second time. Cloning the document unfiltered would re-instantiate
// `free-text`, `unlocked-var` and `contenteditable` inside the sidebar and
// duplicate every `section-id` in the DOM. `ins`/`del` keep all of their
// attributes because the review controller drives accept/reject through
// `diff-index`, `diff-item` and `ng-click` on those markers.
const ANNOTATION_SANITIZE_OPTIONS = {
  allowedTags: [
    'b',
    'strong',
    'i',
    'u',
    'p',
    'span',
    'div',
    'br',
    'img',
    'font',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'colgroup',
    'col',
    'tr',
    'th',
    'td',
    'ins',
    'del',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'crr',
    'crb',
    'sup',
    'sub',
    'lease-var',
  ],
  allowedAttributes: {
    a: ['href', 'name', 'target'],
    img: ['src', 'width', 'height'],
    table: ['cellspacing', 'cellpadding', 'border'],
    td: ['colspan', 'rowspan'],
    span: ['style', 'class', 'var', 'ref'],
    ins: ['*'],
    del: ['*'],
    'lease-var': ['ng-attr-name', 'non-click'],
    '*': ['id', 'style', 'class', 'title', 'var'],
  },
  allowedClasses: {
    span: [
      'word-indent',
      'diff-item',
      'diff-item--static',
      'diff-item--insertion',
      'diff-item--deletion',
      'diff-item--blocked',
      'diff-item--current',
      'diff-item--accepted',
      'diff-item--rejected',
      'diff-item--resolved',
      'diff-item--partial',
      'diff-item--partial--first',
      'diff-item--partial--middle',
      'diff-item--partial--last',
      'transformed--lease-var',
      'transformed--crr',
    ],
  },
  allowedSchemes: ['http', 'https'],
  allowedSchemesByTag: {
    img: ['data', 'http', 'https'],
  },
};

export const ENRICHMENT_STATES = Object.freeze({
  PENDING: 'enrichment_pending',
  NO_POSITION: 'no_position',
  RECOMMENDATION: 'recommendation',
  UNCLASSIFIED: 'unclassified',
  DEAL_TERM: 'deal_term',
});

// The same seven effects, named rather than explained.
//
// The card used to render one full sentence per effect, so three effects cost
// six lines — and the rationale directly below said the same thing concretely
// and better ("Deletes the 200% holdover-rent rate, materially reducing
// Landlord's compensation" against "Caps what the owner can recover from the
// counterparty"). Two registers of one statement. The sentences stay for
// anywhere that needs to teach the taxonomy; the card wears the labels.
const COUNTERPARTY_EFFECT_LABEL = Object.freeze({
  removes_remedy: 'removes a remedy',
  caps_liability: 'caps liability',
  adds_obligation: 'adds an obligation',
  shifts_cost: 'shifts cost',
  narrows_right: 'narrows a right',
  extends_deadline: 'extends a deadline',
  benefits_owner: 'benefits the owner',
  administrative: 'administrative',
});

const COUNTERPARTY_EFFECT_COPY = Object.freeze({
  removes_remedy: 'Removes a remedy available to the owner.',
  caps_liability: 'Caps what the owner can recover from the counterparty.',
  adds_obligation: 'Adds a new obligation for the owner.',
  shifts_cost: 'Changes which party bears a cost.',
  narrows_right: 'Keeps an owner right, but adds limits or conditions.',
  extends_deadline: 'Gives the counterparty more time to act.',
  benefits_owner:
    'Gives the owner something it did not have, or puts a new obligation on the counterparty.',
  administrative: 'Has no substantive effect on the deal.',
});

/**
 * Whether every recognised effect on this change is `administrative`.
 *
 * NOT a severity judgment — severity is already derived from the same effects by
 * `deriveCounterpartySeverity` in counterparty_comment_effects.js, which also
 * overwrites whatever the model claimed. This is the narrower question the
 * severity band cannot answer, because `administrative` maps to `low` and so is
 * indistinguishable there from a real-but-small change.
 *
 * It earns its place because it is the one signal on this card that removes
 * reading rather than adding it: "we read this and it does nothing" means the
 * drafter never has to open the redline. Deliberately distinct from an empty or
 * unrecognised effect list, which means "we could not read this" — opposite
 * messages that must not share a treatment.
 */
export function hasNoSubstantiveEffect(effects) {
  const ids = (Array.isArray(effects) ? effects : []).map(effect =>
    effect && effect.id ? effect.id : effect,
  );
  const recognised = ids.filter(id =>
    Object.prototype.hasOwnProperty.call(EFFECT_SEVERITY, id),
  );

  return (
    recognised.length > 0 && recognised.every(id => id === 'administrative')
  );
}

const EXCERPT_MAX_CHARS = 120;

/**
 * A short plain-text reading of the change this card describes, so that eight
 * stacked boxes in one annotation are eight different boxes. Without it they
 * share a title, a state sentence and often an effect line, and the only way to
 * tell them apart is to open the redline — which is the reading step the card
 * exists to remove.
 *
 * Deliberately built from text already rendered in the document beside it: no
 * model call, nothing to validate, and nothing that can be fabricated. Atoms
 * are unwrapped to their labels rather than stripped, because `⟦f3:Base Rent⟧`
 * reads as "Base Rent" to a drafter and as line noise to everyone.
 */
export function changeExcerpt(html, maxChars = EXCERPT_MAX_CHARS) {
  const text = String(html || '')
    .replace(/⟦[a-z]\d+:([^⟧]*)⟧/g, '$1')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) return null;
  if (text.length <= maxChars) return text;

  const cut = text.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(' ');

  return `${(lastSpace > maxChars * 0.6
    ? cut.slice(0, lastSpace)
    : cut
  ).trimEnd()}…`;
}

/**
 * Where the basis can be checked. The sibling `--generated` box links the
 * clausebook entry behind its own proposal; this box described the
 * counterparty's edits — higher stakes — and linked nothing, so the trust
 * budget ran backwards. The playbook entry for the concept is the one artifact
 * that always exists when a stance was declared.
 *
 * Withheld, like the basis row itself, when there is nothing to point at.
 */
export function recommendationBasisHref(review) {
  const concept = String((review && review.concept) || '').trim();
  if (!concept) return null;

  return `/admin_area/playbooks?concept=${encodeURIComponent(concept)}`;
}

/**
 * The escalation path, as the drafter should read it.
 *
 * An owner is optional by design (lp-010 §4): the targets who lack a seat —
 * outside counsel, a committee — are disproportionately the hard escalations,
 * so a trigger with nobody named is a complete rule and must not read as a
 * degraded version of one with a name.
 *
 * A departed owner is named as departed rather than silently. Sending a drafter
 * to someone who has left is worse than telling her the path is stale, and
 * `users.is_active` is why this is a user reference rather than a string.
 */
export function escalationReading(context) {
  const escalation = context && context.escalation;
  if (!escalation || !String(escalation.trigger || '').trim()) return null;

  return {
    trigger: escalation.trigger,
    owner: escalation.owner || null,
    ownerActive: escalation.ownerActive !== false,
    // NOT "this is past your minimum" — nothing here located the change against
    // a minimum, and claiming it would be the exact failure VOICE.md leads with.
    // What is true is that the firm wrote an escalation rule for this concept;
    // `trigger` carries their own words for when it applies, and she decides.
    message: escalation.owner
      ? `Your playbook escalates this concept. Take it to ${escalation.owner}.`
      : 'Your playbook escalates this concept. Do not decide this alone.',
  };
}

// lp-012 §5. Imported rather than inlined because the arithmetic is worth
// testing on its own: it is the only thing on this card that makes a checkable
// claim about a customer's own numbers.
const COUNTERPARTY_RECOMMENDATION_COPY = Object.freeze({
  accept: 'Accept this change',
  reject: 'Reject this change',
  revise: 'Revise this change',
  adjust_deal_term: 'Adjust the deal term',
});

function readableDealTermName(rawField) {
  const field = String(rawField || '')
    .replace(/\[.*?\]/g, '')
    .replace(/^(?:lease|title)\./, '')
    .split('.')
    .pop();

  if (!field) return 'Deal term';

  const words = field
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Deal term';
}

const ACCOMMODATING_RECOMMENDATIONS = new Set([
  'accept',
  'revise',
  'adjust_deal_term',
]);

function recommendationHistory(context) {
  const observed = context && context.observed;
  if (!observed || typeof observed !== 'object') return null;

  const dealsSeen = observed.n;
  const changedIn = observed.changedIn;
  const outcomeDeals = observed.outcomeDeals;
  const visibility = observed.refusalVisibility;
  const disclaimer = String(observed.refusalDisclaimer || '').trim();
  const validCounts =
    Number.isInteger(dealsSeen) &&
    Number.isInteger(changedIn) &&
    Number.isInteger(outcomeDeals) &&
    dealsSeen >= 0 &&
    outcomeDeals >= 0 &&
    changedIn >= 0 &&
    changedIn <= outcomeDeals;

  if (
    !validCounts ||
    !['complete', 'partial', 'none'].includes(visibility) ||
    visibility === 'none' ||
    (visibility === 'partial' && !disclaimer)
  ) {
    return null;
  }

  return { dealsSeen, changedIn, outcomeDeals, visibility, disclaimer };
}

function withRefusalDisclaimer(basis, history) {
  return history.visibility === 'partial'
    ? `${basis}; ${history.disclaimer}`
    : basis;
}

/**
 * Which way the known-outcome deals actually went, as a strict majority.
 * `changedIn > 0` is the wrong test: one concession among fourteen would make
 * an accept read "both agree" while thirteen of those deals held the line,
 * which asserts a concurrence the aggregate contradicts. An even split has no
 * direction at all, so it returns null and can claim neither agreement nor
 * divergence — the card falls through to showing both sides with the count.
 */
function historyAccommodates(history) {
  if (history.changedIn * 2 > history.outcomeDeals) return true;
  if (history.changedIn * 2 < history.outcomeDeals) return false;

  return null;
}

function recommendationBasis(review, context) {
  // A verdict predating recommendation contexts passed P4-01's playbook gate,
  // so retain that truthful fallback for old/cached responses. Once the server
  // supplies a context, its explicit declaration bit is authoritative.
  const hasPlaybook = context ? context.hasDeclaredStance === true : true;
  const history = recommendationHistory(context);

  // One deal is an anecdote, not a past-deal policy. `n` counts distinct deals
  // and is deliberately checked separately from the known-outcome denominator.
  // The copy names the known-outcome deal rather than the deals seen, because
  // those differ: six deals can carry one recorded decision between them, and
  // "one past deal observed" would then be false.
  if (history && (history.dealsSeen < 2 || history.outcomeDeals < 2)) {
    if (hasPlaybook) return 'your playbook';

    return withRefusalDisclaimer(
      'one past deal with a known outcome — too little history to call a pattern',
      history,
    );
  }

  if (!history) return hasPlaybook ? 'your playbook' : null;

  const historyCount = `${history.changedIn} of ${history.outcomeDeals} past deals`;
  if (!hasPlaybook) {
    return withRefusalDisclaimer(historyCount, history);
  }

  const recommendsAccommodation = ACCOMMODATING_RECOMMENDATIONS.has(
    review.verdict,
  );
  const historyShowsAccommodation = historyAccommodates(history);
  if (recommendsAccommodation === historyShowsAccommodation) {
    return withRefusalDisclaimer('both agree', history);
  }

  const divergent = recommendsAccommodation
    ? `your playbook says concede this; ${historyCount} did`
    : `your playbook says hold this; ${historyCount} conceded it`;
  return withRefusalDisclaimer(divergent, history);
}

/**
 * Turn validated enrichment into the deliberately small view model consumed
 * by the existing annotation provenance box. Effects stay in the plain
 * reading register; a gated verdict becomes a visibly advisory recommendation.
 * Field changes route to deal terms, while unknown concepts remain useful as
 * effect-only readings rather than disappearing from the card.
 *
 * `dealTerm` is design section 5's atom signal, resolved by the caller because
 * only it can see the rendered document:
 *
 *   { field, label, adjustable }
 *
 * `field` is the raw lease-var path and is humanised here; `label` is the
 * editor's own name for that field and is used verbatim when the caller could
 * resolve one. `adjustable` says whether Adjust Manually can actually reach the
 * field — a change can be a deal-term change and still have no clickable
 * lease-var node, and naming the field is worth doing either way.
 */
export function counterpartyCommentCard({
  diffIndex,
  review = null,
  pending = false,
  dealTerm = null,
  recommendationContext = null,
} = {}) {
  if (pending) {
    return {
      diffIndex,
      state: ENRICHMENT_STATES.PENDING,
      title: 'Reading this change',
      stateDescription:
        'Document analysis is still running. You can accept or reject while it finishes.',
      effects: [],
      severity: null,
      noSubstantiveEffect: false,
      rationale: null,
      recommendation: null,
      recommendationBasis: null,
      recommendationBasisHref: null,
      revisedText: null,
      dealTermField: null,
      hasAdjustableField: false,
      tradeFor: null,
      concessionCost: null,
      ladderStatement: null,
      ladderPlacement: null,
      requestedValue: null,
      valueMetric: null,
    };
  }

  if (!review) return null;

  // Read once. Three fields below come from it, and it was being recomputed for
  // each of them.
  const reading = ladderReading(review, recommendationContext);

  const fieldName = dealTerm
    ? String(dealTerm.label || '').trim() ||
      readableDealTermName(dealTerm.field)
    : null;
  const recommendation =
    COUNTERPARTY_RECOMMENDATION_COPY[review.verdict] || null;
  const state = dealTerm
    ? ENRICHMENT_STATES.DEAL_TERM
    : review.unclassified
    ? ENRICHMENT_STATES.UNCLASSIFIED
    : recommendation
    ? ENRICHMENT_STATES.RECOMMENDATION
    : ENRICHMENT_STATES.NO_POSITION;
  const effects = (Array.isArray(review.effects) ? review.effects : [])
    .filter(effect => COUNTERPARTY_EFFECT_COPY[effect])
    .map(effect => ({
      id: effect,
      label: COUNTERPARTY_EFFECT_LABEL[effect],
      description: COUNTERPARTY_EFFECT_COPY[effect],
    }));
  const stateCopy = {
    [ENRICHMENT_STATES.NO_POSITION]: {
      title: 'What this change does',
      stateDescription:
        'No position is recorded for this concept. That is a deliberate playbook gap, not an analysis failure.',
    },
    [ENRICHMENT_STATES.RECOMMENDATION]: {
      title: 'What this change does',
      // The card narrating its own layout. "A playbook-backed recommendation
      // follows this reading" told the reader where to look on a card they were
      // already looking at, and it was the only state whose description said
      // nothing about the change. The other three earn theirs: they explain why
      // there is no recommendation, which is not obvious.
      stateDescription: null,
    },
    [ENRICHMENT_STATES.UNCLASSIFIED]: {
      title: 'What this change does',
      // Was "not in the current vocabulary" — `vocabulary` is the schema's word
      // for the concepts table, and a drafter has no reason to know we keep
      // one. `concept` stays: the no_position state beside this one already
      // uses it, so it is established on this surface.
      stateDescription:
        'This change does not match a known concept. Its effects are still shown.',
    },
    [ENRICHMENT_STATES.DEAL_TERM]: {
      title: 'This changes a deal term',
      stateDescription: `Update ${fieldName} in the deal terms instead of treating this as a text change.`,
    },
  }[state];

  return {
    diffIndex: review.diffIndex,
    state,
    title: stateCopy.title,
    stateDescription: stateCopy.stateDescription,
    effects,
    // Already derived from these same effects upstream — `validateCounterpartyEffects`
    // discards the model's own severity and recomputes it — so this carries a
    // value the list below it always agrees with. That is why the pill has no
    // basis row: it would restate the bullets.
    severity: ['low', 'medium', 'high'].includes(review.severity)
      ? review.severity
      : null,
    noSubstantiveEffect: hasNoSubstantiveEffect(review.effects),
    // An unclassified result is intentionally effect-only (design section
    // 11a); a rationale could smuggle an invented classification back into a
    // state whose purpose is to admit that classification is missing.
    rationale:
      state === ENRICHMENT_STATES.UNCLASSIFIED
        ? null
        : String(review.rationale || '').trim() || null,
    recommendation,
    recommendationBasis: recommendation
      ? recommendationBasis(review, recommendationContext)
      : null,
    recommendationBasisHref: recommendation
      ? recommendationBasisHref(review)
      : null,
    // The firm's own escalation rule for this concept, shown BESIDE the
    // recommendation rather than replacing it.
    //
    // lp-010 §4 describes this firing "when a change is past the concept's
    // minimum". For most concepts nothing still locates a change against a
    // minimum — there is a verdict and a severity and neither is that — so it
    // surfaces the rule the firm wrote, at the moment she is deciding, and lets
    // her apply it. Accompanied, not assisted.
    //
    // lp-012 changes that for the concepts that carry a numeric ladder, and
    // only for those: `ladderStatement` below is the determination lp-010 asked
    // for, made by arithmetic against the customer's own figures rather than
    // asserted. Where a concept has no metric — about half of them — this is
    // null and the escalation rule remains the whole of what the card can say.
    escalation: recommendation
      ? escalationReading(recommendationContext)
      : null,
    // Validation has already refused blank or atom-bearing revise language and
    // the verdict gate has already removed ungrounded prose. This view model
    // carries the accepted value verbatim; it does not locate or rewrite it.
    revisedText: recommendation ? review.revisedText || null : null,
    // lp-012 §4. Read from the recommendation context — which the server builds
    // from the entry and only for a `declared` one — never from the reader's
    // answer. It is customer-authored policy, and the same rule that discards a
    // self-certified Clausebook attribution applies here.
    //
    // Tied to `revise` alone. On an accept there is nothing to trade for, and
    // on a reject the change is going back whatever we would have wanted.
    tradeFor:
      review.verdict === 'revise' && recommendationContext
        ? recommendationContext.tradeFor || null
        : null,
    concessionCost: recommendationContext
      ? recommendationContext.concessionCost || null
      : null,
    // The one claim on this card made by arithmetic rather than by reading.
    // `requestedValue` is the reader's only contribution — what the change asks
    // for, in the concept's own metric — and everything after it is computed
    // here against figures the customer typed.
    ladderStatement: reading.statement,
    ladderPlacement: reading.placement,
    // lp-018 Phase 1. Kept, not just consumed.
    //
    // Everything above computes FROM this number and then dropped it, so the
    // one figure in the exchange that arithmetic can be done on never left the
    // browser. It travels with its unit because a figure without one cannot be
    // compared to a ladder rung later.
    requestedValue: reading.metric === null ? null : review.requestedValue,
    valueMetric: reading.metric,
    dealTermField: fieldName,
    hasAdjustableField: Boolean(dealTerm && dealTerm.adjustable),
  };
}

/**
 * Where this change sits on the concept's ladder, or nothing.
 *
 * Nothing is the ordinary answer: the concept needs a metric, the entry needs
 * to be declared, and the change needs to carry a number the reader could
 * express in that metric. Any one of those missing returns a pair of nulls and
 * the card renders exactly what it rendered before lp-012.
 */
function ladderReading(review, recommendationContext) {
  const ladder = recommendationContext ? recommendationContext.ladder : null;
  const location = locateOnLadder(review.requestedValue, ladder);
  if (!location) return { statement: null, placement: null, metric: null };

  return {
    statement: ladderStatement(location, review.requestedValue),
    placement: location.placement,
    // The unit the figure is in. Null whenever the reading found nothing, which
    // is what stops a bare number being recorded as though it meant something.
    metric: location.metric || null,
  };
}

// One request per hunk half, in document order. The review UI addresses a
// request by `diff-index`, so the diffgram slot is read off the marker rather
// than recomputed here — the two cannot drift apart if only one of them
// decides.
function generatedRequestsForResult(result) {
  return preparedRequests(result)
    .filter(markers => markers.length > 0)
    .map(markers => ({
      index: Number(markers[0].getAttribute('diff-index')),
      markers,
    }))
    .filter(request => Number.isInteger(request.index));
}

export function nextDiffIndex(items) {
  if (!Array.isArray(items)) return 0;
  // Compiler indexes are input-relative, so a rejected patch can leave a hole
  // before a later applied patch. Appending after the array's highest allocated
  // slot prevents the next generated batch from reusing that hole and then
  // colliding with the later request.
  return items.length;
}

export function documentProposalRejectionNotes(routed) {
  return [
    ...((routed && routed.rejected) || []),
    ...((routed && routed.redline && routed.redline.rejected) || []),
  ]
    .map(entry => entry && entry.result && entry.result.note)
    .filter(note => note && note.kind === 'drafter_note');
}

export function mergeDocumentProposalRejectionNotes(
  clientRejections,
  serverNotes,
) {
  return [
    ...(Array.isArray(clientRejections) ? clientRejections : []),
    ...(Array.isArray(serverNotes) ? serverNotes : []),
  ];
}

function generatedDiffgramItem(
  proposal,
  index,
  markers,
  sourceDocument,
  itemData = {},
) {
  const primary = markers.find(marker =>
    marker.classList.contains('diff-item--primary'),
  );
  const provenance = {
    type: 'generated',
    sourceDocument: sourceDocument || 'Imported source document',
    sourceQuote: proposal.sourceQuote,
    rationale: proposal.rationale,
    // Computed server-side by comparing the clause against the source, not
    // self-reported by the model (services_controller.rb,
    // classify_content_provenance). A drafter reads their counterparty's own
    // words and our suggestion very differently, and should.
    contentProvenance: proposal.contentProvenance,
    // False when the citation below did not appear word-for-word in the source.
    // The suggestion is still shown — its lease anchor grounded — but the
    // drafter is told the quote could not be confirmed rather than being left
    // to assume it was.
    sourceQuoteVerified: proposal.sourceQuoteVerified,
    // Kept despite scoring below the review threshold. Nothing here is applied
    // automatically, so the drafter reading it is the check.
    confidenceLow: proposal.confidenceLow,
    // A single value binds the display name and one-click review URL to the
    // exact opaque entry selected server-side. Without all three, the card
    // must not claim Clausebook as its source.
    clausebookEntry: proposal.clausebookEntry,
    // The lease provision a drafted clause followed, kept only when it was
    // verified against the lease server-side. An unverifiable citation is
    // dropped there rather than shown, because it reads as evidence while
    // being the model's word for itself.
    patternedOn: proposal.patternedOn,
    patternedOnVerified: proposal.patternedOnVerified,
    // Set when the term could not be edited into the lease where it belongs and
    // was drafted as a paragraph beside it instead. Without it the drafter sees
    // a rider next to a clause we plainly could have amended and has no way to
    // know we could not.
    draftedBecause: proposal.draftedBecause,
  };

  return {
    ...itemData,
    Type: primary ? primary.tagName.toLowerCase() : 'change',
    _change: itemData._change || 'text',
    _position: itemData._position || 'self',
    _index: index,
    _isBlocked: false,
    _isResolvable: true,
    _showWarnings: false,
    _status: 'unresolved',
    _pid: itemData._pid || (primary ? primary.getAttribute('diff-pid') : ''),
    _generatedMarkers: markers,
    proposal,
    provenance,
  };
}

/**
 * Materialize resolved insert_block proposals through the editor's existing
 * list-item builder. The builder owns template selection and free-text IDs;
 * this layer owns only diffgram allocation, compilation, and provenance.
 */
export async function integrateGeneratedInsertBlocks(
  diffgram,
  routed,
  { baseIndex, insertBlockListItem, compileMarkers, sourceDocument } = {},
) {
  if (!diffgram || typeof diffgram !== 'object') {
    throw new TypeError('integrateGeneratedInsertBlocks requires a diffgram');
  }
  if (!routed || !Array.isArray(routed.blockInsertions)) {
    throw new TypeError(
      'integrateGeneratedInsertBlocks requires routed block insertions',
    );
  }
  if (!Number.isInteger(baseIndex) || baseIndex < 0) {
    throw new TypeError('baseIndex must be a non-negative integer');
  }
  if (typeof insertBlockListItem !== 'function') {
    throw new TypeError('insertBlockListItem is required');
  }

  if (!Array.isArray(diffgram.items)) diffgram.items = [];
  const markers = [];
  const rejected = [];
  let allocated = 0;

  for (
    let insertionIndex = 0;
    insertionIndex < routed.blockInsertions.length;
    insertionIndex += 1
  ) {
    const insertion = routed.blockInsertions[insertionIndex];
    const index = baseIndex + allocated;
    if (diffgram.items[index] !== undefined) {
      throw new Error(`diffgram.items[${index}] is already allocated`);
    }

    const built = await insertBlockListItem({
      anchor: insertion.anchor,
      proposal: insertion.proposal,
      index,
    });

    // A template that cannot carry the block refuses one proposal, not the
    // batch. The rest of this batch is already compiled into the document and
    // still has to reach review, so this becomes a drafter note on the same
    // channel every other refusal uses.
    if (built && built.blocked) {
      rejected.push({
        kind: 'drafter_note',
        reason: built.blocked.reason || 'unsupported_list_template',
        message: built.blocked.message,
        sourceQuote: insertion.proposal.sourceQuote,
        rationale: insertion.proposal.rationale,
      });
      continue;
    }
    if (!built || !built.marker || !built.item) {
      throw new TypeError(
        'insertBlockListItem must return a marker and diffgram item',
      );
    }

    diffgram.items[index] = generatedDiffgramItem(
      insertion.proposal,
      index,
      [built.marker],
      sourceDocument,
      built.item,
    );
    markers.push(built.marker);
    allocated += 1;
  }

  if (markers.length > 0) {
    if (typeof compileMarkers !== 'function') {
      throw new TypeError('compileMarkers is required for inserted blocks');
    }
    compileMarkers(markers);

    // The free-text template renders its `.editable` contenteditable during
    // the compile above, and contentEditableLink binds insertClauseHandler on
    // it, so the marker is listening by the time this runs. A lift whose swap
    // does not land keeps the plain text the builder already put in the block.
    markers.forEach(marker => {
      const item = diffgram.items[Number(marker.getAttribute('diff-index'))];
      if (!item || !hasLiftedClauseHtml(item.proposal)) return;

      const inserted = insertLiftedClause(
        marker.querySelector('.editable'),
        item.proposal,
      );
      if (!inserted) {
        console.warn(
          '[documentProposals] Clausebook lift kept its plain text: the ' +
            'contenteditable was not ready to receive lp:clausebook:insert.',
          item.proposal.clausebookEntry,
        );
      }
    });
  }

  return { markers, rejected };
}

// Mirrors DIFF_CLASSES in lease_editor_controller.js:9275. Only the members
// this module writes are duplicated here.
const DIFF_CLASSES = {
  ITEM: 'diff-item',
  DELETION: 'diff-item--deletion',
  PRIMARY: 'diff-item--primary',
};

/**
 * Stamp the review attributes onto a deletion marker. Extracted from
 * deleteBlock (lease_editor_controller.js:11238) so the production path is the
 * path under test (spec/js/README.md, "Extract first").
 *
 * `list-header` is what routes an accepted deletion through the list
 * bookkeeping at lease_editor_controller.js:8307, which retires the underlying
 * orderedListitem entry so numbering and cross-references recompute as a
 * consequence of the one change. It is therefore read off the marker's own
 * subtree rather than the surrounding container: a nested-paragraph deletion
 * wraps only the nested paragraph, and a complex-block deletion wraps only the
 * section's inner children — in both cases the list number stays in the
 * document, so the marker must not claim to remove a list item.
 */
export function decorateBlockDeletionMarker(
  del,
  { diffgramItem, diffgramIndex },
) {
  del.classList.add(
    DIFF_CLASSES.ITEM,
    DIFF_CLASSES.DELETION,
    DIFF_CLASSES.PRIMARY,
  );
  del.setAttribute('diff-item', `diffgram.items[${diffgramIndex}]`);
  del.setAttribute('diff-index', diffgramIndex);
  del.setAttribute(
    'diff-pid',
    diffgramItem._originalParagraphId || diffgramItem._pid,
  );
  del.setAttribute('diff-type', 'block');
  del.setAttribute(
    'ng-click',
    `annotationsHelper.jumpToRequest(${diffgramIndex})`,
  );
  del.setAttribute('consolidated-pid', diffgramItem._pid);

  if (diffgramItem.Nested !== -1) {
    del.setAttribute('diff-type', 'text');
    diffgramItem._change = 'text';
  } else {
    const list = del.querySelector('[list]');
    if (list) {
      del.setAttribute('list-header', list.getAttribute('list') || 'paragraph');
    }
  }

  diffgramItem._node = del;
  return del;
}

/**
 * Resolve diffgram indices to the DOM nodes accept/reject operates on.
 * Extracted from annotationsHelper.actions.getAllRequestsElements
 * (lease_editor_controller.js:8598).
 *
 * An imported block change keeps its marker on the diffgram item. A generated
 * block request has no `_annotationNode` and its marker is live in the
 * document, so it resolves through `diff-index` like every text change —
 * without that the collector pushed `undefined` and the accept path threw
 * dereferencing `element.classList`.
 */
export function collectRequestElements(
  indices,
  diffgramItems,
  root = document,
) {
  const result = [];

  indices.forEach(index => {
    const item = diffgramItems[index];
    if (item && item._change === 'block' && !item.proposal) {
      result.push(item._node);
      if (item._annotationNode) result.push(item._annotationNode);
      return;
    }
    root
      .querySelectorAll(`[diff-index="${index}"]`)
      .forEach(node => result.push(node));
  });

  return result;
}

/** Resolve the free-text sections the editor must persist for review markers. */
export function reviewMarkerSectionIds(markers) {
  const sectionIds = [];

  markers.forEach(marker => {
    // New row records do not exist before review. Rejecting their INS must not
    // send those minted ids through createBulk, which would create empty
    // free-text records after the cleaner removes the rejected insertion.
    if (
      marker.matches('ins[data-table-row-change="insertion"]') &&
      marker.classList.contains('diff-item--rejected')
    ) {
      return;
    }
    const editable = marker.closest('.editable');
    const editables = editable
      ? [editable]
      : marker.querySelectorAll('.editable');

    editables.forEach(element => {
      sectionIds.push(element.getAttribute('section-id'));
    });
  });

  return sectionIds;
}

export function buildGeneratedBlockDeletion(
  { index, pid, proposal, target },
  deleteBlock,
) {
  if (typeof deleteBlock !== 'function') {
    throw new TypeError('deleteBlock is required');
  }
  const section = target.querySelector('[section-id]');
  const item = {
    Block: true,
    Html: target.innerHTML,
    Nested: -1,
    Section: section ? section.getAttribute('section-id') : pid,
    _change: 'block',
    _pid: pid,
    _position: 'self',
    proposal,
  };

  deleteBlock(item, index, null, target);
  if (!item._node) {
    throw new Error('deleteBlock did not create a deletion marker');
  }
  return { item, marker: item._node };
}

/** Hand resolved delete_block proposals to the editor's existing deleteBlock. */
export function integrateGeneratedDeleteBlocks(
  diffgram,
  routed,
  { baseIndex, deleteBlockItem, compileMarkers, sourceDocument } = {},
) {
  if (!diffgram || typeof diffgram !== 'object') {
    throw new TypeError('integrateGeneratedDeleteBlocks requires a diffgram');
  }
  if (!routed || !Array.isArray(routed.blockDeletions)) {
    throw new TypeError(
      'integrateGeneratedDeleteBlocks requires routed block deletions',
    );
  }
  if (!Number.isInteger(baseIndex) || baseIndex < 0) {
    throw new TypeError('baseIndex must be a non-negative integer');
  }
  if (typeof deleteBlockItem !== 'function') {
    throw new TypeError('deleteBlockItem is required');
  }

  if (!Array.isArray(diffgram.items)) diffgram.items = [];
  const markers = [];

  routed.blockDeletions.forEach((deletion, deletionIndex) => {
    const index = baseIndex + deletionIndex;
    if (diffgram.items[index] !== undefined) {
      throw new Error(`diffgram.items[${index}] is already allocated`);
    }

    const built = deleteBlockItem({
      index,
      pid: deletion.pid,
      proposal: deletion.proposal,
      target: deletion.target,
    });
    if (!built || !built.marker || !built.item) {
      throw new TypeError(
        'deleteBlockItem must return a marker and diffgram item',
      );
    }

    diffgram.items[index] = generatedDiffgramItem(
      deletion.proposal,
      index,
      [built.marker],
      sourceDocument,
      built.item,
    );
    markers.push(built.marker);
  });

  if (markers.length > 0) {
    if (typeof compileMarkers !== 'function') {
      throw new TypeError('compileMarkers is required for deleted blocks');
    }
    compileMarkers(markers);
  }

  return { markers, rejected: [] };
}

function directTableCells(row) {
  return Array.from(row.children).filter(cell => cell.matches('td, th'));
}

function replaceEditableContent(editable, content) {
  const controlMarkers = Array.from(editable.children).filter(child =>
    child.matches('.SOCE, .EOCE'),
  );
  editable.replaceChildren(...controlMarkers);
  const end = controlMarkers.find(marker => marker.classList.contains('EOCE'));
  editable.insertBefore(
    document.createTextNode(unescapeFromProjection(content)),
    end || null,
  );
}

function decorateTableRowMarker(marker, item, index, type) {
  marker.className = `${DIFF_CLASSES.ITEM} diff-item--${type} ${DIFF_CLASSES.PRIMARY}`;
  marker.setAttribute('diff-item', `diffgram.items[${index}]`);
  marker.setAttribute('diff-index', String(index));
  marker.setAttribute('diff-pid', item._pid);
  marker.setAttribute('diff-type', 'block');
  marker.setAttribute('data-table-row-change', type);
  marker.setAttribute('ng-click', `annotationsHelper.jumpToRequest(${index})`);
  item._node = marker;
  return marker;
}

function tableRowLandingSite(anchor) {
  let cursor = anchor;
  while (
    cursor.nextElementSibling?.matches('ins[data-table-row-change="insertion"]')
  ) {
    cursor = cursor.nextElementSibling;
  }
  return cursor;
}

/** Clone one static tr and mint a distinct free-text id for every new cell. */
export function buildGeneratedRowInsertion(
  { anchor, template, proposal, index },
  { getNextFreeTextValue, getNewPid },
) {
  if (
    typeof getNextFreeTextValue !== 'function' ||
    typeof getNewPid !== 'function'
  ) {
    throw new TypeError('free-text and pid allocators are required');
  }

  const row = template.cloneNode(true);
  row
    .querySelectorAll('[id]')
    .forEach(element => element.removeAttribute('id'));
  row.querySelectorAll('[pid]').forEach(element => {
    element.setAttribute('pid', getNewPid());
  });
  const cells = directTableCells(row);
  if (cells.length !== proposal.cells.length) {
    throw new RangeError('insert_row cell count does not match its template');
  }

  const sectionIds = cells.map((cell, cellIndex) => {
    const freeText = cell.querySelector('[free-text]');
    const editable = freeText?.querySelector('.editable[section-id]');
    if (!freeText || !editable) {
      throw new RangeError('insert_row template cell is not editable');
    }

    const sectionId = String(getNextFreeTextValue());
    freeText.setAttribute('free-text', sectionId);
    freeText.querySelectorAll('[section-id]').forEach(element => {
      element.setAttribute('section-id', sectionId);
    });
    replaceEditableContent(editable, proposal.cells[cellIndex]);
    return sectionId;
  });

  const marker = document.createElement('ins');
  const pid = row.querySelector('[pid]')?.getAttribute('pid') || getNewPid();
  const item = {
    Section: sectionIds[0],
    _change: 'block',
    _pid: pid,
    _position: 'after',
    _rowSectionIds: sectionIds,
    _rowAnchorFreeTextIds: Array.from(
      anchor.querySelectorAll('[free-text]'),
    ).map(element => element.getAttribute('free-text')),
  };
  decorateTableRowMarker(marker, item, index, 'insertion');
  marker.append(row);
  tableRowLandingSite(anchor).after(marker);
  return { item, marker, row, sectionIds };
}

/** Wrap one resolved static tr in the existing block-review contract. */
export function buildGeneratedRowDeletion({ target, index }) {
  const section = target.querySelector('[section-id]');
  const pid = target.querySelector('[pid]')?.getAttribute('pid') || '';
  const item = {
    Section: section?.getAttribute('section-id') || '',
    _change: 'block',
    _pid: pid,
    _position: 'self',
  };
  const marker = document.createElement('del');
  decorateTableRowMarker(marker, item, index, 'deletion');
  target.before(marker);
  marker.append(target);
  return { item, marker, row: target };
}

/** Materialize routed insert_row/delete_row proposals as review requests. */
export function integrateGeneratedTableRows(
  diffgram,
  routed,
  {
    baseIndex,
    getNextFreeTextValue,
    getNewPid,
    compileMarkers,
    sourceDocument,
  } = {},
) {
  if (!diffgram || typeof diffgram !== 'object') {
    throw new TypeError('integrateGeneratedTableRows requires a diffgram');
  }
  if (
    !routed ||
    !Array.isArray(routed.rowInsertions) ||
    !Array.isArray(routed.rowDeletions)
  ) {
    throw new TypeError('integrateGeneratedTableRows requires routed rows');
  }
  if (!Number.isInteger(baseIndex) || baseIndex < 0) {
    throw new TypeError('baseIndex must be a non-negative integer');
  }
  if (!Array.isArray(diffgram.items)) diffgram.items = [];

  const markers = [];
  const changes = [
    ...routed.rowInsertions.map(change => ({ type: 'insert', change })),
    ...routed.rowDeletions.map(change => ({ type: 'delete', change })),
  ];

  changes.forEach(({ type, change }, changeIndex) => {
    const index = baseIndex + changeIndex;
    if (diffgram.items[index] !== undefined) {
      throw new Error(`diffgram.items[${index}] is already allocated`);
    }
    const built =
      type === 'insert'
        ? buildGeneratedRowInsertion(
            { ...change, index },
            { getNextFreeTextValue, getNewPid },
          )
        : buildGeneratedRowDeletion({ ...change, index });
    diffgram.items[index] = generatedDiffgramItem(
      change.proposal,
      index,
      [built.marker],
      sourceDocument,
      built.item,
    );
    markers.push(built.marker);
  });

  if (markers.length > 0) {
    if (typeof compileMarkers !== 'function') {
      throw new TypeError('compileMarkers is required for table rows');
    }
    compileMarkers(markers);
  }
  return { markers, rejected: [] };
}

// Written for the drafter deciding how hard to read the clause, so it names
// whose words these are rather than which check produced the label.
const CONTENT_PROVENANCE_LABELS = {
  lifted: 'Taken from the source document',
  adapted: "The source document's wording, with names matched to this lease",
  drafted: 'Drafted to match the lease',
};

// "Drafted to match the lease" is a claim, and it is only true when the clause
// actually followed a provision we could find. Where it did not, saying so is
// the difference between a suggestion the drafter can weigh and one that
// flatters itself.
const UNMATCHED_DRAFT_LABEL =
  'Drafted — not matched to existing lease language';

function validClausebookEntry(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;

  const id = String(entry.id || '').trim();
  const name = String(entry.name || '').trim();
  const rawUrl = String(entry.url || '').trim();
  if (!id || !name || !rawUrl) return null;

  try {
    const baseUrl = new URL(String(window._clausebook_base_url || ''));
    const url = new URL(rawUrl, baseUrl);
    if (!/^https?:$/.test(url.protocol) || url.origin !== baseUrl.origin) {
      return null;
    }
    return { id, name, url: url.href };
  } catch (_error) {
    return null;
  }
}

function attributedClausebookEntry(provenance) {
  if (!['lifted', 'adapted'].includes(provenance.contentProvenance))
    return null;
  return validClausebookEntry(provenance.clausebookEntry);
}

function originLabel(provenance) {
  if (attributedClausebookEntry(provenance)) {
    return provenance.contentProvenance === 'lifted'
      ? 'Taken from Clausebook'
      : 'Adapted from Clausebook';
  }

  if (provenance.contentProvenance !== 'drafted') {
    return CONTENT_PROVENANCE_LABELS[provenance.contentProvenance];
  }

  return provenance.patternedOnVerified
    ? CONTENT_PROVENANCE_LABELS.drafted
    : UNMATCHED_DRAFT_LABEL;
}

function provenanceHtml(provenance, label) {
  const panel = document.createElement('aside');
  panel.className = 'annotation__provenance annotation__provenance--generated';
  panel.setAttribute('data-provenance', 'document-proposal');

  const heading = document.createElement('strong');
  heading.className = 'annotation__provenance-title';
  heading.textContent = label
    ? `Suggested change — ${label}`
    : 'Suggested change';
  panel.appendChild(heading);

  if (provenance.confidenceLow) {
    const caution = document.createElement('span');
    caution.className =
      'annotation__provenance-origin annotation__provenance-origin--unmatched';
    caution.textContent =
      'Read this one closely — a weaker reading of the source';
    panel.appendChild(caution);
  }

  // Ahead of the origin badge, because it explains the shape of the suggestion
  // rather than the source of its words: the drafter's first question about a
  // rider sitting next to an amendable-looking clause is why it is not an
  // amendment.
  if (provenance.draftedBecause) {
    const because = document.createElement('span');
    because.className =
      'annotation__provenance-origin annotation__provenance-origin--unmatched';
    because.textContent = provenance.draftedBecause;
    panel.appendChild(because);
  }

  const origin = originLabel(provenance);
  if (origin) {
    const badge = document.createElement('span');
    const state =
      provenance.contentProvenance === 'drafted' &&
      !provenance.patternedOnVerified
        ? 'unmatched'
        : provenance.contentProvenance;
    badge.className = `annotation__provenance-origin annotation__provenance-origin--${state}`;
    badge.textContent = origin;
    panel.appendChild(badge);
  }

  const clausebookEntry = attributedClausebookEntry(provenance);
  if (clausebookEntry) {
    const row = document.createElement('div');
    row.className = 'annotation__provenance-row';
    const term = document.createElement('strong');
    term.textContent = 'Clausebook source: ';
    const link = document.createElement('a');
    link.className = 'annotation__provenance-link';
    link.href = clausebookEntry.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = clausebookEntry.name;
    link.setAttribute(
      'aria-label',
      `Open Clausebook entry ${clausebookEntry.name}`,
    );
    row.append(term, link);
    panel.appendChild(row);
  }

  // The source document's name is deliberately not rendered. The drafter
  // uploaded it a minute ago and every card in the batch would repeat the same
  // filename — twenty-six times in a real run. It stays on `provenance` for the
  // audit trail (generatedChangeAuditProvenance), which is where a reader who
  // does need it is looking.
  [
    [
      provenance.sourceQuoteVerified === false
        ? 'Source quote (could not be confirmed)'
        : 'Source quote',
      provenance.sourceQuote,
    ],
    ['Rationale', provenance.rationale],
    // Shown so the fit can be judged in one glance instead of taken on trust.
    ['Patterned on', provenance.patternedOn],
  ].forEach(([label, value]) => {
    if (!String(value || '').trim()) return;
    const row = document.createElement('div');
    row.className = 'annotation__provenance-row';
    const term = document.createElement('strong');
    term.textContent = `${label}: `;
    row.append(term, document.createTextNode(String(value || '')));
    panel.appendChild(row);
  });

  return panel.outerHTML;
}

/**
 * The block a request's markers live in — the unit a card is built around.
 *
 * Text markers sit inside an existing `[pid]`. A block marker owns the `[pid]`
 * it inserts or deletes, so the marker itself is the snapshot and the
 * navigation target until acceptance resolves it.
 */
function containerFor(item) {
  const primary = (item._generatedMarkers || []).find(marker =>
    marker.classList.contains('diff-item--primary'),
  );

  return (
    (primary &&
      (primary.closest('[pid]') ||
        primary.closest('ins[diff-type="block"]') ||
        primary.closest('del[diff-type="block"]'))) ||
    null
  );
}

/**
 * An inert snapshot of one block, showing every change the card covers.
 *
 * Markers belonging to OTHER cards are still stripped — an insertion is removed
 * and a deletion unwrapped — so a paragraph carrying an unrelated tenant
 * request does not present it as part of this suggestion.
 */
function annotationDocumentHtml(items, container) {
  if (!container) return null;

  const covered = new Set(items.map(item => item._index));
  const clone = container.cloneNode(true);

  // Drop what the document does not show. A lease template carries both
  // branches of every conditional and hides the one it did not take, so a raw
  // clone reads "the DevelopmentShopping Center" where the document reads "the
  // Shopping Center" — the card quoting a paragraph that exists nowhere, least
  // of all in the text the model was given.
  //
  // The branch is hidden in the document by a stylesheet rule scoped to a
  // `.lease` ancestor, which is why this went unnoticed: it looks handled until
  // the markup is read somewhere that rule does not reach. Removing the nodes
  // is not a second way of hiding them, it is the same decision the index
  // already made.
  //
  // ANNOTATION_HIDDEN_SUBTREE_SELECTOR, not SKIPPED_SUBTREE_SELECTOR: the
  // projection additionally skips `.word-indent`, which carries no indexable
  // characters but is drawn — it is the gap between a list marker and the text
  // it labels. Reusing the projection's list here closed "(i)" up against its
  // own clause and shifted every justified line after it.
  clone
    .querySelectorAll(ANNOTATION_HIDDEN_SUBTREE_SELECTOR)
    .forEach(hidden => hidden.remove());

  clone.querySelectorAll('.diff-item').forEach(marker => {
    if (covered.has(Number(marker.getAttribute('diff-index')))) return;

    if (marker.tagName === 'INS') {
      marker.remove();
    } else {
      marker.replaceWith(...marker.childNodes);
    }
  });

  return safeSanitize(
    clone.outerHTML.replace(/{{.*?}}/g, ''),
    ANNOTATION_SANITIZE_OPTIONS,
  );
}

/**
 * A short name for one change, so its evidence box can say which of the
 * paragraph's changes it explains.
 *
 * Two green highlights above two boxes leaves the reader inferring the mapping.
 * The longest inserted fragment is the most identifying thing a change has —
 * "or subsidiary" for one edit, "provided any subtenant operates a restaurant"
 * for its neighbour — and it is what a drafter would point at to name it.
 */
const CHANGE_LABEL_LIMIT = 48;

// The request that best names a group of them: a patch is a deletion request
// and an insertion request, and the insertion carries the wording a reader
// recognises.
function labelSource(items) {
  return (
    items.find(item =>
      (item._generatedMarkers || []).some(marker => marker.tagName === 'INS'),
    ) || items[0]
  );
}

function changeLabel(item) {
  const markers = item._generatedMarkers || [];
  const fragments = markers
    .filter(marker => marker.tagName === 'INS')
    .map(marker => marker.textContent.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const removals = markers
    .filter(marker => marker.tagName === 'DEL')
    .map(marker => marker.textContent.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const longest = [...fragments, ...removals].sort(
    (a, b) => b.length - a.length,
  )[0];
  if (!longest) return '';

  const prefix = fragments.length === 0 ? 'removes ' : '';
  const text =
    longest.length > CHANGE_LABEL_LIMIT
      ? `${longest.slice(0, CHANGE_LABEL_LIMIT).trimEnd()}…`
      : longest;

  return `${prefix}“${text}”`;
}

function primaryMarker(item) {
  return (item._generatedMarkers || []).find(marker =>
    marker.classList.contains('diff-item--primary'),
  );
}

/**
 * Order two requests the way the drafter reads them.
 *
 * `annotation.index` is what Previous/Next walks
 * (lease_editor_controller.js:8888), and it was allocation order: the model's
 * proposal order, then a batch of block insertions, then deletions, then table
 * rows, then every drafting note. None of that is where the changes sit on the
 * page, so Next jumped from the top of the lease to the bottom and back.
 *
 * The diff-index is deliberately untouched — it is baked into `diff-item`
 * attributes and every marker in the document — so only the card's position in
 * the sequence moves. The comments sidebar renders in insertion order too
 * (ngRepeat over an object iterates keys unsorted, angular.js:33999), so the
 * visible list and the navigation now agree.
 */
function byDocumentPosition(left, right) {
  const leftMarker = primaryMarker(left);
  const rightMarker = primaryMarker(right);
  if (!leftMarker || !rightMarker || leftMarker === rightMarker) {
    return left._index - right._index;
  }

  const relation = leftMarker.compareDocumentPosition(rightMarker);
  // CONTAINED_BY sets FOLLOWING as well, which is the answer either way: a
  // marker nested inside another starts after it.
  if (relation & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
  if (relation & Node.DOCUMENT_POSITION_PRECEDING) return 1;
  // Disconnected markers have no reading order. Fall back to allocation order
  // rather than 0, so the comparator stays total.
  return left._index - right._index;
}

function nextAnnotationIndex(annotations) {
  return Object.values(annotations).reduce(
    (next, annotation) =>
      Number.isInteger(annotation && annotation.index)
        ? Math.max(next, annotation.index + 1)
        : next,
    0,
  );
}

/**
 * Build the annotation-card model for generated redlines. Generated requests
 * use one card each, even when several requests target the same paragraph, so
 * every request keeps its own evidence and accept/reject lifecycle.
 */
export function createGeneratedAnnotations(
  diffgram,
  annotations,
  { setDiffgramItemActions } = {},
) {
  if (!diffgram || !Array.isArray(diffgram.items)) {
    throw new TypeError('createGeneratedAnnotations requires diffgram items');
  }
  if (!annotations || typeof annotations !== 'object') {
    throw new TypeError('createGeneratedAnnotations requires annotations');
  }
  if (typeof setDiffgramItemActions !== 'function') {
    throw new TypeError('setDiffgramItemActions is required');
  }

  // A skip here is invisible from the outside and looks exactly like a bug
  // report we could not reproduce: the marker is already compiled into the
  // document, so the drafter sees a redline with no card and no Suggested
  // change box beneath it, while nothing in the console says why. Name the
  // item and the reason instead of returning silently.
  const skip = (item, reason) => {
    if (typeof console !== 'undefined') {
      console.warn(
        `[documentProposals] no review card for diff-index ${item &&
          item._index} (${reason}). ` +
          'Its marker is in the document but cannot be accepted or rejected.',
        { pid: item && item._pid, proposal: item && item.proposal },
      );
    }
    return false;
  };

  const actual = [];
  const orderedItems = diffgram.items
    .filter(item => {
      if (!item) return false;
      if (!item.proposal || !item.provenance) {
        return skip(item, 'the item carries no proposal or provenance');
      }
      if (!String(item.provenance.sourceQuote || '').trim()) {
        return skip(
          item,
          'the proposal cited nothing from the source document',
        );
      }
      return true;
    })
    .sort(byDocumentPosition);

  // One card per block, not per request. Two edits to one paragraph used to
  // produce two cards, each showing the whole paragraph again — which reads as
  // the same suggestion duplicated rather than two separate ones. The evidence
  // still belongs to each change; only the paragraph is shared, and each box
  // names the change it explains.
  //
  // Keyed on the container element rather than the pid: a block insertion's
  // container is its own marker, so it must not be folded in with a text patch
  // that happens to sit in the same paragraph.
  const groups = new Map();
  orderedItems.forEach(item => {
    const container = containerFor(item);
    if (!container) {
      skip(item, 'its marker resolved to no container element');
      return;
    }

    if (!groups.has(container)) groups.set(container, []);
    groups.get(container).push(item);
  });

  groups.forEach((items, container) => {
    const snapshot = annotationDocumentHtml(items, container);
    if (!snapshot) return;

    items.forEach(setDiffgramItemActions);

    const lead = items[0];
    const key = `${lead._pid}--generated-${lead._index}`;
    // The proposed text leads and the citation supports it. Provenance first
    // put the model's own explanation above the change it is explaining, which
    // reads as the system arguing for itself before showing its work.
    //
    // One box per proposal, not per request. A patch that rewrites a paragraph
    // is now many requests — one per hunk, so each can be accepted on its own —
    // and every one of them carries the same citation. Printing it eight times
    // would bury the paragraph under its own footnotes.
    const evidenceGroups = [];
    items.forEach(item => {
      const provenance = item.provenance || {};
      const signature = `${provenance.sourceQuote ||
        ''}\u0000${provenance.rationale || ''}`;
      const existing = evidenceGroups.find(
        group => group.signature === signature,
      );
      if (existing) {
        existing.items.push(item);
        return;
      }
      evidenceGroups.push({ signature, provenance, items: [item] });
    });

    const evidence = evidenceGroups
      .map(group =>
        provenanceHtml(
          group.provenance,
          // Named only when there is another box to tell it apart from. The
          // label comes from whichever of the group's requests says most about
          // it — the words being added, where a patch both removes and adds,
          // since "removes X" describes a replacement less well than the
          // replacement does.
          evidenceGroups.length > 1
            ? changeLabel(labelSource(group.items))
            : '',
        ),
      )
      .join('');
    const html = `${snapshot}${evidence}`;
    const dummy = document.createElement('dummy');
    dummy.innerHTML = html;
    const annotation = {
      container,
      pid: key,
      targetPid: lead._pid,
      type: 'diff',
      items,
      html,
      status: 'unresolved',
      // A card is blocked only if every change in it is; one unresolvable edit
      // must not take its neighbours' Accept away.
      isBlocked: items.every(item => item._isBlocked),
      isResolvable: items.some(item => item._isResolvable),
      isManageable: false,
      // One past the highest in use, not the count. The two agree until a card
      // is removed — INSERT HERE retires one every time it relocates a
      // suggestion — after which the count points at an index that is already
      // taken, and two cards answer to one `data-index`. Navigation addresses
      // them by that attribute.
      index: nextAnnotationIndex(annotations),
      _showAcceptReject: items.some(item => item._showAcceptReject),
      _showAdjustManually: items.some(item => item._showAdjustManually),
      _showWarnings: items.some(item => item._showWarnings),
      provenance: lead.provenance,
      insertions: dummy.querySelectorAll('ins.diff-item--primary'),
      deletions: dummy.querySelectorAll('del.diff-item--primary'),
    };

    annotations[key] = annotation;
    items.forEach(item => {
      item._annotation = annotation;
    });
    actual.push(
      ...dummy.querySelectorAll(
        '.diff-item.diff-item--primary:not(.diff-item--static)',
      ),
    );
  });

  return actual;
}

export function generatedChangeAuditProvenance(annotation, request) {
  const provenance =
    (request && request.provenance) || (annotation && annotation.provenance);
  if (!provenance || provenance.type !== 'generated') return null;

  return {
    type: provenance.type,
    sourceDocument: provenance.sourceDocument,
    sourceQuote: provenance.sourceQuote,
    rationale: provenance.rationale,
    contentProvenance: provenance.contentProvenance,
    patternedOn: provenance.patternedOn,
    patternedOnVerified: provenance.patternedOnVerified,
    sourceQuoteVerified: provenance.sourceQuoteVerified,
    confidenceLow: provenance.confidenceLow,
    clausebookEntry: provenance.clausebookEntry,
  };
}

/**
 * Attach compiler output to the existing Version Control data and Angular
 * binding contracts. The base index must be the same one supplied to
 * routeDocumentProposals; rejected patches may leave unused indexes because
 * later compiler results retain their input-relative diff-index.
 */
export function integrateGeneratedRedline(
  diffgram,
  routed,
  proposals,
  { baseIndex, compileMarkers, onRejected, sourceDocument } = {},
) {
  if (!diffgram || typeof diffgram !== 'object') {
    throw new TypeError('integrateGeneratedRedline requires a diffgram');
  }
  if (!routed || !routed.redline) {
    throw new TypeError('integrateGeneratedRedline requires routed redlines');
  }
  if (!Number.isInteger(baseIndex) || baseIndex < 0) {
    throw new TypeError('baseIndex must be a non-negative integer');
  }

  if (!Array.isArray(diffgram.items)) diffgram.items = [];
  const textPatches = (proposals || []).filter(
    proposal =>
      proposal &&
      proposal.kind === 'text_patch' &&
      String(proposal.sourceQuote || '').trim(),
  );
  const compiledMarkers = [];

  routed.redline.results.forEach((result, patchIndex) => {
    generatedRequestsForResult(result).forEach(({ index, markers }) => {
      if (index < baseIndex) {
        throw new Error(
          `diffgram.items[${index}] precedes the base index ${baseIndex}`,
        );
      }
      if (diffgram.items[index] !== undefined) {
        throw new Error(`diffgram.items[${index}] is already allocated`);
      }
      // Every request from one patch carries that patch's evidence. The card
      // groups them back by paragraph and shows the citation once.
      diffgram.items[index] = generatedDiffgramItem(
        textPatches[patchIndex],
        index,
        markers,
        sourceDocument,
      );
      compiledMarkers.push(...markers);
    });
  });

  if (compiledMarkers.length > 0) {
    if (typeof compileMarkers !== 'function') {
      throw new TypeError('compileMarkers is required for emitted redlines');
    }
    compileMarkers(compiledMarkers);
  }

  // The controller enters review mode only after createGeneratedAnnotations
  // has populated the toolbar counts and the comments sidebar model.

  const rejected = documentProposalRejectionNotes(routed);
  if (typeof onRejected === 'function') onRejected(rejected);

  return { markers: compiledMarkers, rejected };
}

// What the panel says when the review did not run.
//
// A verdict needs an owner side AND a declared position on the concept. With
// neither, the model still answers — concept, effects and rationale — and the
// panel renders those exactly as it renders advised ones. A test import came
// back with four hundred and forty descriptions and no verdict, on a document
// where the counterparty had cut holdover from 200% to 110% and deleted the
// outside commencement date. Nothing on screen separated that from a review
// that found nothing to object to.
//
// The server now refuses the request instead, and this names the thing to fix.
// Returns null for an unrecognised reason, so a server that learns a new one
// before this does says nothing rather than something wrong.
const COUNTERPARTY_UNAVAILABLE = {
  owner_side_missing: {
    title: 'These changes were not reviewed.',
    detail:
      'LeasePilot has no record of which side of the table you are on, so it cannot tell whether a change is in your favour or against you. An administrator can set it on this form or on the company.',
  },
  // Not "it failed" — the request is still out there, and if it lands the panel
  // still fills in. This only explains why the loaders came down.
  took_too_long: {
    title: 'Still reading these changes.',
    detail:
      'This is taking longer than usual. The reading is still running and the panel will fill in if it arrives; you can accept or reject in the meantime.',
  },
  no_declared_positions: {
    title: 'These changes were not reviewed.',
    detail:
      'Your playbook has no confirmed position for this kind of document. Confirm at least one and LeasePilot will advise on the concepts it covers, and describe the rest.',
  },
};

export function counterpartyUnavailableNotice(reason) {
  return COUNTERPARTY_UNAVAILABLE[reason] || null;
}

// One negotiating point, one card.
//
// The differ splits a single edit across adjacent runs, and the reader answers
// per run because that is the contract it was given. A test import produced four
// cards for one transfer fee — "Reduces the nonrefundable transfer-review fee
// from $2,000 to $1,000", four times, once for the deleted "Two", once for the
// inserted "One", once for each half of the figure — and five for one insurance
// limit. The reader is not at fault: it sees the whole clause, the tokens being
// inline in continuous prose. It is the output that is per-fragment.
//
// The grouping key is the diffgram's own: insertions carry `PreSection` and
// deletions carry `Section`, and both halves of one edit carry the same number.
// Checked against a real import — 485-488 all read 1621, 489/490 both 1729 — and
// it does not over-merge: the two insurance changes stay apart at 1008 and 1074.
//
// Two guards. Runs must be CONSECUTIVE, so a section number reused far apart in
// the document cannot drag unrelated changes together. And reviews must AGREE on
// verdict, because a disagreement between fragments is information — hiding it
// behind whichever one we happened to pick would be worse than two cards.
function reviewSectionKey(item) {
  if (!item) return '';
  return String(item.Section || item.PreSection || '').trim();
}

function mergeReviewGroup(group, items) {
  if (group.length === 1) return group[0];

  // The union, not the pick. Choosing a representative's effects would mean
  // discarding a reader finding on a heuristic — and the fragment whose label
  // looks wrong may be the one that is right. Fragments do currently contradict
  // each other (a deletion half of an increase came back `narrows_right` while
  // the insertion half came back `shifts_cost`), but that is a labelling problem
  // from reading a run in isolation, and it belongs to the pass that re-measures
  // effects once an owner side is configured. Not to this one, quietly.
  const effects = [];
  group.forEach(review => {
    (review.effects || []).forEach(effect => {
      if (!effects.includes(effect)) effects.push(effect);
    });
  });

  // The longest rationale is the one that described the whole edit rather than
  // its own run — "Increases the required limit from $1M to $2M" against
  // "Deletes the word One".
  const spokesman = group.reduce((best, review) =>
    String(review.rationale || '').length > String(best.rationale || '').length
      ? review
      : best,
  );

  // What the counterparty is ASKING for is what they inserted; the deletion
  // carries the number being replaced. A ladder compared against the old figure
  // would read the drafter's own position back as the request.
  const requested = group.find(
    review =>
      review.requestedValue !== null &&
      review.requestedValue !== undefined &&
      items[review.diffIndex] &&
      items[review.diffIndex].Type === 'ins',
  );

  return {
    ...spokesman,
    effects,
    requestedValue: requested
      ? requested.requestedValue
      : spokesman.requestedValue,
    coveredDiffIndexes: group.map(review => review.diffIndex),
  };
}

/**
 * lp-018 Phase 1. The figure a decision on this change should record, if any.
 *
 * Cards are coalesced: the differ splits one edit across adjacent runs, the
 * reader answers per run, and one card ends up speaking for several diff
 * indexes. `card.diffIndex` is the index it speaks AS.
 *
 * The figure is attached to that row and no other. A card covering three
 * changes, writing three ledger rows each carrying the same number, would have
 * one counterparty request counted three times in every distribution built on
 * the ledger afterwards — and the whole reason the number is being kept is to
 * do arithmetic on it later.
 *
 * Both fields or neither. A number with no unit cannot be compared to a ladder
 * rung, and storing it anyway would look like evidence.
 */
export function figureForChange(annotation, request) {
  const entries = (annotation && annotation._counterpartyReadCards) || [];
  const index = request && request._index;
  const entry = entries.find(
    candidate =>
      candidate &&
      candidate.card &&
      candidate.card.diffIndex === index &&
      candidate.card.requestedValue !== null &&
      candidate.card.requestedValue !== undefined &&
      candidate.card.valueMetric,
  );

  if (!entry) return {};

  return {
    requestedValue: entry.card.requestedValue,
    valueMetric: entry.card.valueMetric,
  };
}

export function coalesceCounterpartyReviews(reviews, items = []) {
  if (!Array.isArray(reviews) || !reviews.length) return [];

  const ordered = reviews.slice().sort((a, b) => a.diffIndex - b.diffIndex);
  const groups = [];

  ordered.forEach(review => {
    const key = reviewSectionKey(items[review.diffIndex]);
    const open = groups[groups.length - 1];
    const joins =
      open &&
      key &&
      open.key === key &&
      open.verdict === (review.verdict || '') &&
      open.last === review.diffIndex - 1;

    if (joins) {
      open.reviews.push(review);
      open.last = review.diffIndex;
      return;
    }

    groups.push({
      key,
      verdict: review.verdict || '',
      last: review.diffIndex,
      reviews: [review],
    });
  });

  return groups.map(group => mergeReviewGroup(group.reviews, items));
}
